import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, GlobalStyles, Typography } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import LogoutIcon from '@mui/icons-material/Logout';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Media from '@core/dashboard/components/AppLoader/Media';
import ParticipantService from '../../Dolby/ParticipantService';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '../../../../providers';
import { useMytaStreams } from '../MytaStreamsProvider';
import { useCinemaProgress } from './useCinemaProgress';
import { buildCinemaPalette } from './palette';
import { getCinemaStyles, cinemaGlobalStyles } from './styles';
import {
  AUTO_ENTER_CTA_DELAY_MS,
  CINEMA_FONTS_LINK_ID,
  CINEMA_FONTS_URL,
  CINEMA_PHASES,
  CINEMA_TIPS,
  KEEP_TAB_HINT,
  LOADING_HEADLINE,
  READY_HEADLINE,
  SEGMENT_GAP_PX,
  STEP_FADE_MS,
  TIP_FADE_MS,
  TIP_ROTATE_MS,
} from './constants';

const formatEta = (totalSec: number): string => {
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const LoadingCinema = () => {
  const { appTheme, user, navigateToEventsPage } = useMytaverse();
  const { currentEvent, setShowControls } = useMytaverseEvent();
  const { togglePlayStream, playStream } = useMytaStreams();
  const { percent, phaseIdx, ready, done, etaSec, error, hintOverride } =
    useCinemaProgress();

  const palette = useMemo(() => buildCinemaPalette(appTheme), [appTheme]);
  const classes = useMemo(() => getCinemaStyles(palette), [palette]);

  const [displayedPhaseIdx, setDisplayedPhaseIdx] = useState(phaseIdx);
  const [stepVisible, setStepVisible] = useState(true);
  const [tipIdx, setTipIdx] = useState(0);
  const [tipVisible, setTipVisible] = useState(true);
  const [showCta, setShowCta] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(
    () => !!document.fullscreenElement,
  );

  const autoEnterAttemptedRef = useRef(false);
  const tipSwapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (document.getElementById(CINEMA_FONTS_LINK_ID)) {
      return;
    }

    const link = document.createElement('link');
    link.id = CINEMA_FONTS_LINK_ID;
    link.rel = 'stylesheet';
    link.href = CINEMA_FONTS_URL;
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    setShowControls(false);

    return () => {
      setShowControls(true);
    };
  }, []);

  useEffect(() => {
    if (phaseIdx === displayedPhaseIdx) {
      return;
    }

    setStepVisible(false);
    const timeout = setTimeout(() => {
      setDisplayedPhaseIdx(phaseIdx);
      setStepVisible(true);
    }, STEP_FADE_MS);

    return () => clearTimeout(timeout);
  }, [phaseIdx, displayedPhaseIdx]);

  useEffect(() => {
    if (done || error) {
      return;
    }

    const interval = setInterval(() => {
      setTipVisible(false);
      tipSwapTimeoutRef.current = setTimeout(() => {
        setTipIdx((idx) => (idx + 1) % CINEMA_TIPS.length);
        setTipVisible(true);
      }, TIP_FADE_MS);
    }, TIP_ROTATE_MS);

    return () => {
      clearInterval(interval);
      if (tipSwapTimeoutRef.current) {
        clearTimeout(tipSwapTimeoutRef.current);
      }
    };
  }, [done, error]);

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleChange);
    };
  }, []);

  useEffect(() => {
    if (!ready || playStream || error || autoEnterAttemptedRef.current) {
      return;
    }

    autoEnterAttemptedRef.current = true;
    togglePlayStream();
    const timeout = setTimeout(() => setShowCta(true), AUTO_ENTER_CTA_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [ready, playStream, error, togglePlayStream]);

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      return;
    }

    document.documentElement.requestFullscreen?.();
  }, []);

  const handleLeave = useCallback(() => {
    navigateToEventsPage({});
  }, [navigateToEventsPage]);

  const initials = useMemo(() => {
    const fullName =
      [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
      user?.name ||
      user?.email ||
      '';

    return ParticipantService.buildInitials(fullName) ?? '';
  }, [user]);

  const segments = useMemo(() => {
    const bounds = CINEMA_PHASES.map((phase) => phase.threshold).concat(100);

    return CINEMA_PHASES.map((phase, i) => {
      const start = bounds[i];
      const end = bounds[i + 1];
      const fillFraction = done
        ? 1
        : Math.max(0, Math.min(1, (percent - start) / (end - start)));

      return {
        label: phase.label,
        left: `calc(${start}% + ${i === 0 ? 0 : SEGMENT_GAP_PX / 2}px)`,
        right: `calc(${100 - end}% + ${
          i === CINEMA_PHASES.length - 1 ? 0 : SEGMENT_GAP_PX / 2
        }px)`,
        fillPercent: fillFraction * 100,
      };
    });
  }, [percent, done]);

  const headline = error
    ? error.headline
    : done
    ? READY_HEADLINE
    : LOADING_HEADLINE;
  const displayedPercent = done ? 100 : Math.floor(percent);
  const tip = CINEMA_TIPS[tipIdx];

  return (
    <Box className="myta-cinema" sx={classes.root}>
      <GlobalStyles styles={cinemaGlobalStyles} />

      <Box sx={classes.media}>
        <Media loadingAsset={currentEvent?.loadingScreenAsset} />
      </Box>

      <Box sx={classes.topBlur} />
      <Box sx={classes.topScrim} />
      <Box sx={classes.railBlur} />
      <Box sx={classes.railScrim} />

      {appTheme?.customerLogo ? (
        <Box sx={classes.logoWrap}>
          {typeof appTheme.customerLogo === 'string' ? (
            <Box
              component="img"
              src={appTheme.customerLogo}
              alt=""
              sx={classes.logoImg}
            />
          ) : (
            <Box sx={classes.logoNode}>{appTheme.customerLogo}</Box>
          )}
        </Box>
      ) : null}

      <Box sx={classes.sys}>
        <Box
          component="button"
          sx={classes.sysBtn}
          onClick={handleFullscreen}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </Box>
        {initials ? <Box sx={classes.avatar}>{initials}</Box> : null}
        <Box
          component="button"
          sx={classes.sysBtn}
          onClick={handleLeave}
          title="Leave"
        >
          <LogoutIcon />
        </Box>
      </Box>

      {!done && !error ? (
        <Box sx={{ ...classes.tip, opacity: tipVisible ? 1 : 0 }}>
          <Typography component="span" sx={classes.tipTag}>
            TIP
          </Typography>
          <Typography component="span" sx={classes.tipText}>
            {tip.before}
            <b>{tip.strong}</b>
            {tip.after}
          </Typography>
        </Box>
      ) : null}

      {done && showCta && !playStream ? (
        <Box component="button" sx={classes.cta} onClick={togglePlayStream}>
          Enter experience
          <ArrowForwardIcon />
        </Box>
      ) : null}

      <Box sx={classes.left}>
        <Box sx={classes.stepLine}>
          <Box
            sx={
              done || error
                ? { ...classes.stepDot, ...classes.stepDotStatic }
                : classes.stepDot
            }
          />
          <Typography
            component="span"
            sx={{ ...classes.stepName, opacity: stepVisible ? 1 : 0 }}
          >
            {error ? error.reason : CINEMA_PHASES[displayedPhaseIdx].label}
          </Typography>
          {!error ? (
            <Typography component="span" sx={classes.stepCnt}>
              step {displayedPhaseIdx + 1} of {CINEMA_PHASES.length}
            </Typography>
          ) : null}
        </Box>
        <Typography component="h1" sx={classes.headline}>
          {headline}
        </Typography>
        {!error ? (
          <Box sx={classes.hint}>
            <InfoOutlinedIcon />
            <span>{hintOverride ?? KEEP_TAB_HINT}</span>
          </Box>
        ) : null}
      </Box>

      {!error ? (
        <Box sx={classes.right}>
          <Box sx={classes.pct}>
            {displayedPercent}
            <small>%</small>
          </Box>
          <Typography sx={classes.eta}>
            ready in ≈ <b>{formatEta(etaSec)}</b>
          </Typography>
        </Box>
      ) : null}

      <Box sx={classes.bar}>
        {segments.map((segment) => (
          <Box
            key={segment.label}
            sx={{
              ...classes.segTrack,
              left: segment.left,
              right: segment.right,
            }}
          >
            <Box
              sx={{ ...classes.segFill, width: `${segment.fillPercent}%` }}
            />
          </Box>
        ))}
        {!done && !error ? (
          <Box sx={{ ...classes.head, left: `${percent}%` }} />
        ) : null}
      </Box>
    </Box>
  );
};

export default LoadingCinema;
